/**
 * Auditoría del Portal Condominio: bitácora de eventos reales de las cuentas de mantenimiento
 * (`get_condominio_bitacora`, migración 20261016010000). El autor solo existe cuando la tabla de
 * origen lo guarda; los pagos registrados y las multas no lo tienen.
 */
import type { StatusTone } from "@/components/ui/status-pill";

export type TipoEvento =
  | "pago_registrado" | "pago_eliminado" | "pago_devuelto"
  | "multa_registrada" | "titular_cambiado" | "solicitud_resuelta";

export const TIPOS_EVENTO: TipoEvento[] = [
  "pago_registrado", "pago_eliminado", "pago_devuelto", "multa_registrada", "titular_cambiado", "solicitud_resuelta",
];

export const ETIQUETA_EVENTO: Record<TipoEvento, string> = {
  pago_registrado: "Pago registrado",
  pago_eliminado: "Pago eliminado",
  pago_devuelto: "Pago devuelto",
  multa_registrada: "Multa registrada",
  titular_cambiado: "Cambio de titular",
  solicitud_resuelta: "Solicitud resuelta",
};

export const TONO_EVENTO: Record<TipoEvento, StatusTone> = {
  pago_registrado: "success",
  pago_eliminado: "danger",
  pago_devuelto: "warning",
  multa_registrada: "warning",
  titular_cambiado: "info",
  solicitud_resuelta: "neutral",
};

export const esTipoEvento = (v: string): v is TipoEvento => (TIPOS_EVENTO as string[]).includes(v);

export interface EventoBitacora {
  /** Clave estable para la tabla (la RPC no tiene id propio). */
  clave: string;
  fecha: string;
  tipo: TipoEvento;
  id_cuenta: number | null;
  unidad: string | null;
  descripcion: string;
  monto: number | null;
  actor: string | null;
}

export function normalizarEvento(f: Record<string, unknown>, i: number): EventoBitacora {
  const tipo = String(f.tipo);
  return {
    clave: `${tipo}-${f.id_cuenta ?? "x"}-${f.fecha}-${i}`,
    fecha: String(f.fecha ?? ""),
    tipo: esTipoEvento(tipo) ? tipo : "pago_registrado",
    id_cuenta: f.id_cuenta != null ? Number(f.id_cuenta) : null,
    unidad: f.unidad != null ? String(f.unidad) : null,
    descripcion: String(f.descripcion ?? ""),
    monto: f.monto != null ? Number(f.monto) : null,
    actor: f.actor != null && String(f.actor) !== "" ? String(f.actor) : null,
  };
}

/** Conteo por tipo para los KPIs y los chips del filtro. */
export function conteoPorTipo(eventos: EventoBitacora[]): Record<TipoEvento, number> {
  const c = Object.fromEntries(TIPOS_EVENTO.map((t) => [t, 0])) as Record<TipoEvento, number>;
  for (const e of eventos) c[e.tipo] += 1;
  return c;
}

/** Fecha y hora en CDMX, como la ve el equipo. */
const FMT = new Intl.DateTimeFormat("es-MX", {
  timeZone: "America/Mexico_City", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
});
export const fechaHoraCdmx = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "Sin fecha" : FMT.format(d);
};
